import { trackError } from '@billy/track-error'
import { v4 as uuidv4 } from 'uuid'

const SEGMENT_TRACK_ENDPOINT = 'https://api.segment.io/v1/t'

/**
 * Sends a Segment track event that survives the page being closed, reloaded, or navigated away from to another site.
 *
 * Use it instead of `track` from `useSegment` only for events fired from `beforeunload` / `pagehide` handlers.
 * `analytics.track` sends an XHR that the browser cancels once the page starts unloading, so those events are lost
 * (or only arrive intermittently). In-app navigation is unaffected, because the page stays alive and `track` works.
 *
 * Example: the expert reports "Time Spent" event (`useTrackExpertReportEntry`) has to report how long the user stayed
 * on a report. Leaving the report through an in-app link sends it with `track`, but closing the tab or reloading
 * dropped it, so on unload it is sent through this helper with the same payload instead.
 *
 * Constraints of `navigator.sendBeacon`:
 * - No custom headers, so the write key goes in the body.
 * - The body must be a `text/plain` Blob without a charset, otherwise some browsers drop the request.
 * - `type` and `messageId` are set by hand because Segment rejects events without them.
 */
export const sendTrackEventBeacon = (event: string, properties: Record<string, unknown>) => {
  const writeKey = window.ENV?.SEGMENT_WRITE_KEY || process.env.SEGMENT_WRITE_KEY
  const segmentUser = globalThis.analytics?.user?.()
  const anonymousId = segmentUser?.anonymousId() || undefined
  const userId = segmentUser?.id() || undefined

  if (!writeKey || !navigator.sendBeacon || (!anonymousId && !userId)) {
    return
  }

  try {
    const sentAt = new Date().toISOString()
    const body = JSON.stringify({
      ...(anonymousId ? { anonymousId } : {}),
      context: {
        locale: navigator.language,
        page: {
          path: globalThis.location.pathname,
          referrer: document.referrer,
          search: globalThis.location.search,
          title: document.title,
          url: globalThis.location.href,
        },
        screen: {
          density: globalThis.devicePixelRatio,
          height: globalThis.screen.height,
          width: globalThis.screen.width,
        },
        userAgent: navigator.userAgent,
      },
      event,
      messageId: `ajs-beacon-${uuidv4()}`,
      properties,
      sentAt,
      timestamp: sentAt,
      type: 'track',
      ...(userId ? { userId } : {}),
      writeKey,
    })

    navigator.sendBeacon(SEGMENT_TRACK_ENDPOINT, new Blob([body], { type: 'text/plain' }))
  } catch (error) {
    trackError(error)
  }
}
